import { useRef } from "react";

import useVideoTracker from "hooks/useVideoTracker";
import { jjLogger } from "utils/logUtils";
import { isFunction } from "utils/helperUtils";
import { trackEventVideoRenderedError } from "utils/gAnalyticsUtils";
import TemplatePresenter from "presenters/TemplatePresenter";
import MakePresenter from "presenters/MakePresenter";

import useMakes from "./useMakes";

const useWebGlPlayer = (params) => {
  const { make } = useMakes();
  const transportRef = useRef(null);
  const playerRef = useRef(null);
  const { handleResetsOnPlay, resetProgressTracking, handleTimeUpdateTrackers } = useVideoTracker(params.templateGroup);

  const logError = (message) => {
    jjLogger.logError(`WebGL Player Error: ${message}`);
    trackEventVideoRenderedError("WebGL");
  };

  const logDebug = (message) => {
    jjLogger.logDebug(`useWebGlPlayer.js: ${message}`);
  };

  const onEnded = () => {
    resetProgressTracking();
  };

  const init = ({ onLoaded, onFailed, onWebGlError }) => {
    import("webgl-render-library")
      .then((renderLib) => {
        const { SYPlayer, Transport } = renderLib;

        logDebug("initializing SYPlayer");
        try {
          playerRef.current = new SYPlayer(
            params,
            ({ currentTime, positionData, templateData, feature, isPlaying, isFullScreen, frameNumber }) => {
              const player = {
                currentTime,
                positionData,
                templateData,
                templateName: TemplatePresenter.name(MakePresenter.template(make)),
                isStreaming: feature.includes(".m3u8"),
                isPlaying,
                isFullScreen,
                frameNumber,
                deviceInfo: navigator.userAgent,
              };
              throw new Error(`Context lost. Player: ${JSON.stringify(player)}`);
            },
          );

          playerRef.current.addEventListener("loaded", (event) => {
            logDebug("SYPlayer 'loaded' triggered, initializing Transport");
            transportRef.current = new Transport(event.currentTarget);
            if (isFunction(onLoaded)) onLoaded(event);
            const { videoPlayer } = event.currentTarget.resources.video;

            videoPlayer.onended = onEnded;
            videoPlayer.ontimeupdate = handleTimeUpdateTrackers;
            videoPlayer.onplay = handleResetsOnPlay;
          });
        } catch (error) {
          if (isFunction(onWebGlError)) onWebGlError(error);
          logError(error.message);
        }
      })
      .catch((error) => {
        if (isFunction(onFailed)) onFailed(error);
        logError(error.message);
      });
  };

  const cleanup = () => {
    if (!playerRef.current) return;

    playerRef.current.removeEventListener("loaded", null);
    playerRef.current.destroy();

    if (transportRef.current) {
      transportRef.current.destroy();
    }

    playerRef.current = null;
    transportRef.current = null;
  };

  const exitFullscreen = () => transportRef.current.closeFullScreen();

  const pausePlayer = () => {
    if (playerRef?.current) {
      playerRef.current.pause();
    }
  };

  const resetPlayer = () => {
    playerRef.current.seekTo(0);
    playerRef.current.pause();
  };

  return { init, cleanup, playerRef, exitFullscreen, pausePlayer, resetPlayer };
};

export default useWebGlPlayer;
